import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { PageEvent } from "@angular/material/paginator";
import { Sort } from "@angular/material/sort";
import { Router } from "@angular/router";
import { ConfigurationService } from "@core/services/configuration/configuration.service";
import { ApiService } from "@shared/services/api/api.service";
import { NgxNotifyService } from "@shared/services/ngx-notify/ngx-notify.service";
import { SearchResearchPersonnel } from "../researchpersonnel/models/research-personnel-report";
import { Observable } from "rxjs";
import { ResearchPersonnelReportList } from "../researchpersonnel/models/research-personnel-report-list";
import { SearchPrincipalInvestigator } from "../principal-investigator/models/principal-investigator-report";

@Injectable({ providedIn: 'root' })
export class ResearchPersonnelReportService extends ApiService {
    private readonly STORAGE_KEY = 'researchPersonnelReport.filters.v1';
    form!: FormGroup;
    // table state
    pageIndex = 1;  // 1-based
    pageSize = 10;
    sort: 'FirstName' | 'LastName' | 'Institution' | 'Email' | 'Role' | 'IRBNumber' = 'LastName';
    order: 'asc' | 'desc' = 'asc';

    constructor(
        private httpClient: HttpClient,
        config: ConfigurationService,
        notify: NgxNotifyService,
        router: Router,
        fb: FormBuilder
    ) {
        super(httpClient, config, notify, router, fb);
        this.form = fb.group({                    // <-- use `fb`, NOT `this.fb`
            firstName: [''],
            lastName: [''],
            institutionId: [null as number | null],
            irbNumber: [''],
            studyTitle: [''],
            roleId: [null as number | null],
            startDate: [''],
            endDate: [''],
            email: [''],
            phone: [''],
            principalInvestigatorFirstName: [''],
            principalInvestigatorLastName: [''],
            principalInvestigatorEmail: [''],
            principalInvestigatorPhone: [''],
        });

    }

    save(): void {
        const { firstName, lastName, institutionId } = this.form.getRawValue();
        const state = {
            firstName: (firstName || '').trim(),
            lastName: (lastName || '').trim(),
            institutionId: institutionId ?? null,
            pageIndex: this.pageIndex,
            pageSize: this.pageSize,
            sort: this.sort,
            order: this.order
        };
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
    }

    clear(): void {
        localStorage.removeItem(this.STORAGE_KEY);
        this.form.reset({ firstName: '', lastName: '', institutionId: null }, { emitEvent: false });
        this.pageIndex = 1; this.pageSize = 10; this.sort = 'LastName'; this.order = 'asc';
    }


    handleSortChange(sort: Sort, fetch: () => void): void {
        switch (sort.active) {
            case 'firstName': this.sort = 'FirstName'; break;
            case 'lastName': this.sort = 'LastName'; break;
            case 'institution': this.sort = 'Institution'; break;
            case 'email': this.sort = 'Email'; break;
            case 'role': this.sort = 'Role'; break;
            case 'irbNumber': this.sort = 'IRBNumber'; break;
            default: this.sort = 'LastName';
        }
        this.order = (sort.direction || 'asc') as 'asc' | 'desc';
        this.pageIndex = 1;
        this.save();
        fetch();
    }

    handlePage(evt: PageEvent, fetch: () => void): void {
        this.pageIndex = (evt.pageIndex || 0) + 1;
        this.pageSize = evt.pageSize;
        this.save();
        fetch();
    }


    // service
    getReport(dto: SearchResearchPersonnel): Observable<ResearchPersonnelReportList> {
        const parts: string[] = [];
        if (dto.firstName) parts.push(`firstName=${encodeURIComponent(dto.firstName)}`);
        if (dto.lastName) parts.push(`lastName=${encodeURIComponent(dto.lastName)}`);
        if (dto.institutionId && dto.institutionId > 0) parts.push(`institutionId=${dto.institutionId}`);
        if (dto.roleId && dto.roleId > 0) parts.push(`roleId=${dto.roleId}`);
        if (dto.irbNumber) parts.push(`irbNumber=${encodeURIComponent(dto.irbNumber)}`);
        if (dto.studyTitle) parts.push(`studyTitle=${encodeURIComponent(dto.studyTitle)}`);
        if (dto.startDate) parts.push(`startDate=${encodeURIComponent(dto.startDate)}`);
        if (dto.endDate) parts.push(`endDate=${encodeURIComponent(dto.endDate)}`);
        if (dto.email) parts.push(`email=${encodeURIComponent(dto.email)}`);
        if (dto.phone) parts.push(`phone=${encodeURIComponent(dto.phone)}`);
        if (dto.principalInvestigatorFirstName) parts.push(`piFirstName=${encodeURIComponent(dto.principalInvestigatorFirstName)}`);
        if (dto.principalInvestigatorLastName) parts.push(`piLastName=${encodeURIComponent(dto.principalInvestigatorLastName)}`);
        if (dto.principalInvestigatorEmail) parts.push(`piEmail=${encodeURIComponent(dto.principalInvestigatorEmail)}`);
        if (dto.principalInvestigatorPhone) parts.push(`piPhone=${encodeURIComponent(dto.principalInvestigatorPhone)}`);
        const filter = parts.join('&');
        // use the correct route name here:
        return this.searchReport<ResearchPersonnelReportList>(
            'report/ResearchPersonnel',
            filter,
            dto.sort || 'LastName',
            dto.order || 'asc',
            (dto.pageIndex || 1) - 1,
            dto.pageSize || 10
        );
    }


    exportExcel(dto: SearchResearchPersonnel): Observable<Blob> {
        const body: SearchResearchPersonnel = {
            ...dto,
            forExcel: true,
            pageIndex: 1,
            pageSize: 1_000_000
        };
        let filterQueryParts: string[] = [];
        if (dto.firstName) {
            filterQueryParts.push(`firstName=${encodeURIComponent(dto.firstName)}`);
        }
        if (dto.lastName) {
            filterQueryParts.push(`lastName=${encodeURIComponent(dto.lastName)}`);
        }
        if (dto.institutionId && dto.institutionId > 0) {
            filterQueryParts.push(`institutionId=${dto.institutionId}`);
        }
        if (dto.roleId && dto.roleId > 0) {
            filterQueryParts.push(`roleId=${dto.roleId}`);
        }
        if (dto.irbNumber) {
            filterQueryParts.push(`irbNumber=${encodeURIComponent(dto.irbNumber)}`);
        }
        if (dto.studyTitle) {
            filterQueryParts.push(`studyTitle=${encodeURIComponent(dto.studyTitle)}`);
        }
        if (dto.startDate) {
            filterQueryParts.push(`startDate=${encodeURIComponent(dto.startDate)}`);
        }
        if (dto.endDate) {
            filterQueryParts.push(`endDate=${encodeURIComponent(dto.endDate)}`);
        }
        if (dto.email) {
            filterQueryParts.push(`email=${encodeURIComponent(dto.email)}`);
        }
        if (dto.phone) {
            filterQueryParts.push(`phone=${encodeURIComponent(dto.phone)}`);
        }
        if (dto.principalInvestigatorFirstName) {
            filterQueryParts.push(`piFirstName=${encodeURIComponent(dto.principalInvestigatorFirstName)}`);
        }
        if (dto.principalInvestigatorLastName) {
            filterQueryParts.push(`piLastName=${encodeURIComponent(dto.principalInvestigatorLastName)}`);
        }
        if (dto.principalInvestigatorEmail) {
            filterQueryParts.push(`piEmail=${encodeURIComponent(dto.principalInvestigatorEmail)}`);
        }
        if (dto.principalInvestigatorPhone) {
            filterQueryParts.push(`piPhone=${encodeURIComponent(dto.principalInvestigatorPhone)}`);
        }
        
        const filterQuery = filterQueryParts.join('&');

        return this.downloadExcelFile(`report/ResearchPersonnel?${filterQuery}&forExcel=true`);
    }




}